import {
  HHAlert,
  HHBox,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { OpenInNew, ReceiptLongOutlined } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { buildBillManagementUrl } from '../components/bills/bill-management/bill-management-navigator';
import ClientDiscountsList from '../components/clients/client-discounts';
import ClientInsurerList, {
  ClientInsurerWithContract,
} from '../components/clients/client-insurer-list';
import LoadingComponent from '../components/utils/loading';
import {
  RecordPanel,
  RecordPanelDestination,
  RecordPanelDestinations,
  RecordPanelExternalLink,
  RecordPanelField,
  RecordPanelHeading,
  RecordPanelTitle,
} from '../components/utils/record-panel';
import {
  leftPanelAdminPanelAction,
  leftPanelBillsDestination,
  leftPanelClient,
  leftPanelIdentifier,
} from '../constants/strings/left-panel-labels';
import {
  ContractType,
  useGetClientInsurersByIdsQuery,
  useGetContractsV2Query,
  useGetPartnershipsQuery,
} from '../types';
import {
  findActiveContract,
  getMostExpensiveBillingModelName,
} from '../utils/contract-utils';

export const CLIENT_BY_ID_CONTAINER_TEST_ID = 'client-by-id-container';
export const CLIENT_PANEL_TEST_ID = 'client-main-heading';
export const CLIENT_PANEL_ADMIN_LINK_TEST_ID = 'client-panel-admin-link';
export const CLIENT_PANEL_VIEW_BILLS_LINK_TEST_ID =
  'client-panel-view-bills-link';

const ClientByIdPage = (): JSX.Element => {
  const params = useParams<{ id: string }>();
  const clientId = parseInt(params.id ?? '0', 10);
  const {
    data: clientInsurersData,
    loading: loadingClientInsurers,
    error: errorClientInsurers,
  } = useGetClientInsurersByIdsQuery({
    variables: { clientIds: [clientId] },
  });

  const {
    data: contractData,
    loading: contractLoading,
    error: contractError,
  } = useGetContractsV2Query({
    variables: {
      clientIds: [clientId],
      page: 1,
      size: 100,
    },
  });

  const {
    data: partnershipsData,
    loading: partnershipsLoading,
    error: partnershipsError,
  } = useGetPartnershipsQuery({
    variables: { type: 'billing' },
  });

  if (contractLoading || loadingClientInsurers || partnershipsLoading) {
    return (
      <HHBox sx={{ width: '100%' }}>
        <LoadingComponent center />
      </HHBox>
    );
  }

  if (contractError || errorClientInsurers || partnershipsError) {
    return (
      <HHAlert hhVariant="outlined" severity="error">
        <HHTypography hhVariant="body">
          There was an error loading client insurer list
        </HHTypography>
      </HHAlert>
    );
  }

  const clientInsurers = clientInsurersData?.getClientInsurersByIds;
  const contracts = contractData?.getContractsV2?.items ?? [];
  const partnerships = partnershipsData?.getPartnerships ?? [];

  if (!clientInsurers) {
    return (
      <HHStack spacing={2} overflow="hidden">
        <HHTypography hhVariant="body">Client info not found</HHTypography>
      </HHStack>
    );
  }

  const clientInfo = clientInsurers[0].client;

  const partnershipIds = new Set(
    contracts.map(c => c.partnershipId).filter(Boolean) as number[],
  );

  const getPartnershipName = (
    partnershipId: number | null | undefined,
  ): string | null =>
    partnerships.find(p => p.id === partnershipId)?.name ?? null;

  const clientInsurersWithContract: ClientInsurerWithContract[] =
    clientInsurers.map(clientInsurer => {
      const ciContracts = contracts.filter(
        contract => contract.clientsInsurerId === clientInsurer.id,
      );
      const activeContract = findActiveContract(ciContracts as ContractType[]);

      return {
        ...clientInsurer,
        mostExpensiveBillingModelName:
          getMostExpensiveBillingModelName(
            activeContract?.contractPackages ?? [],
          ) ?? '',
        activeContractStartDate: activeContract?.startDate ?? '',
        activeContractEndDate: activeContract?.endDate ?? null,
        contractBillingType: activeContract?.billingType ?? '',
        activeContractPartnershipName: getPartnershipName(
          activeContract?.partnershipId,
        ),
      };
    });

  return (
    <HHStack
      data-testid={CLIENT_BY_ID_CONTAINER_TEST_ID}
      height={'100%'}
      direction="row"
    >
      <RecordPanel testId={CLIENT_PANEL_TEST_ID}>
        <RecordPanelHeading label={leftPanelClient} id={`Id ${clientId}`}>
          <RecordPanelTitle>{clientInfo.name}</RecordPanelTitle>
        </RecordPanelHeading>

        <HHStack marginTop={5}>
          <RecordPanelField
            label={leftPanelIdentifier}
            value={clientInfo.identifier}
          />
        </HHStack>

        <RecordPanelDestinations>
          {/* Client-level: every bill across all of this client's insurers. */}
          <RecordPanelDestination
            testId={CLIENT_PANEL_VIEW_BILLS_LINK_TEST_ID}
            icon={<ReceiptLongOutlined sx={{ fontSize: 16 }} />}
            href={buildBillManagementUrl({ clientIds: [clientId] })}
          >
            {leftPanelBillsDestination}
          </RecordPanelDestination>
        </RecordPanelDestinations>

        <RecordPanelExternalLink
          testId={CLIENT_PANEL_ADMIN_LINK_TEST_ID}
          icon={<OpenInNew sx={{ fontSize: 14 }} />}
          onClick={(): void => {
            const baseAdminPanelUrl =
              process.env.REACT_APP_ADMIN_PANEL_URL || '';

            window.open(
              `${baseAdminPanelUrl}/admin/clients/${clientId}`,
              '_blank',
            );
          }}
        >
          {leftPanelAdminPanelAction}
        </RecordPanelExternalLink>
      </RecordPanel>
      <HHStack direction="column" gap={4} padding={4} flex={1}>
        <ClientInsurerList data={clientInsurersWithContract} />
        <ClientDiscountsList
          clientId={clientId}
          partnershipIds={
            partnershipIds ? Array.from(partnershipIds) : undefined
          }
        />
      </HHStack>
    </HHStack>
  );
};

export default ClientByIdPage;
